import React, { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type Connection,
  type OnConnect,
  type OnReconnect,
  type OnNodesChange,
  type OnEdgesChange,
  BackgroundVariant,
  MarkerType,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomNode from './CustomNode';
import type { NodeTypeInfo, WorkflowDef, NodeResult } from '../types';

interface CanvasProps {
  workflow: WorkflowDef | null;
  nodeTypes: Record<string, NodeTypeInfo>;
  activeNodes: Record<string, NodeResult>;
  onWorkflowChange: (wf: WorkflowDef) => void;
  onNodeSelect: (nodeId: string | null) => void;
}

const rfNodeTypes = { custom: CustomNode };

function isEditableElement(element: Element | null): boolean {
  if (!element) return false;
  const tag = element.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (element as HTMLElement).isContentEditable
  );
}

export default function Canvas({
  workflow,
  nodeTypes,
  activeNodes,
  onWorkflowChange,
  onNodeSelect,
}: CanvasProps) {
  const { screenToFlowPosition } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Sync canvas only when fields visible in the graph changed.
  // Prompt/config edits should not reset layout.
  const canvasSyncKey = useMemo(() => {
    if (!workflow) return '';
    return JSON.stringify({
      id: workflow.id,
      nodes: workflow.nodes.map(n => ({
        id: n.id,
        type: n.type,
        label: n.label,
        position: n.position,
        domain: n.config?.domain || '',
      })),
      edges: workflow.edges,
    });
  }, [workflow]);

  React.useEffect(() => {
    if (!workflow) {
      setNodes([]);
      setEdges([]);
      return;
    }

    setNodes(
      workflow.nodes.map(n => ({
        id: n.id,
        type: 'custom',
        position: n.position,
        data: {
          label: n.label,
          nodeType: n.type,
          domain: n.config?.domain || '',
          routeDescription: n.config?.route_description || '',
          color: nodeTypes[n.type]?.color || '#6366f1',
          active: null,
          isRunning: false,
        },
      })),
    );

    setEdges(
      workflow.edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.condition || '',
        animated: false,
        style: { stroke: '#475569', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
        labelStyle: { fill: '#94a3b8', fontSize: 10 },
      })),
    );
  }, [canvasSyncKey, workflow, nodeTypes, setNodes, setEdges]);

  // Apply execution highlights without rebuilding node positions.
  React.useEffect(() => {
    setNodes(prevNodes => {
      let changed = false;
      const nextNodes = prevNodes.map(node => {
        const nextActive = activeNodes[node.id] || null;
        const currentActive = (node.data as { active?: NodeResult | null }).active || null;
        if (currentActive === nextActive) return node;
        changed = true;
        return {
          ...node,
          data: {
            ...node.data,
            active: nextActive,
          },
        };
      });
      return changed ? nextNodes : prevNodes;
    });

    setEdges(prevEdges => {
      let changed = false;
      const nextEdges = prevEdges.map(edge => {
        const shouldAnimate = !!activeNodes[edge.source];
        if (edge.animated === shouldAnimate) return edge;
        changed = true;
        return {
          ...edge,
          animated: shouldAnimate,
        };
      });
      return changed ? nextEdges : prevEdges;
    });
  }, [activeNodes, setNodes, setEdges]);

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (!workflow) return;
      if (!connection.source || !connection.target) return;

      const exists = workflow.edges.some(
        e => e.source === connection.source && e.target === connection.target,
      );
      if (exists) return;

      const newEdge = {
        id: `e-${connection.source}-${connection.target}-${Date.now()}`,
        source: connection.source,
        target: connection.target,
      };

      onWorkflowChange({
        ...workflow,
        edges: [...workflow.edges, newEdge],
      });
    },
    [workflow, onWorkflowChange],
  );

  const handleNodesChange: OnNodesChange<Node> = useCallback(
    changes => {
      if (isEditableElement(document.activeElement) && changes.some(change => change.type === 'remove')) {
        return;
      }
      onNodesChange(changes);
    },
    [onNodesChange],
  );

  // Persist position only at drag end.
  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, draggedNode: Node) => {
      if (!workflow) return;
      const newNodes = workflow.nodes.map(n =>
        n.id === draggedNode.id
          ? {
              ...n,
              position: {
                x: draggedNode.position.x,
                y: draggedNode.position.y,
              },
            }
          : n,
      );
      onWorkflowChange({ ...workflow, nodes: newNodes });
    },
    [workflow, onWorkflowChange],
  );

  const handleEdgesChange: OnEdgesChange<Edge> = useCallback(
    changes => {
      if (isEditableElement(document.activeElement) && changes.some(change => change.type === 'remove')) {
        return;
      }
      onEdgesChange(changes);
    },
    [onEdgesChange],
  );

  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      if (!workflow || deletedEdges.length === 0) return;
      if (isEditableElement(document.activeElement)) return;
      const deletedIds = new Set(deletedEdges.map(e => e.id));
      onWorkflowChange({
        ...workflow,
        edges: workflow.edges.filter(e => !deletedIds.has(e.id)),
      });
    },
    [workflow, onWorkflowChange],
  );

  const onReconnect: OnReconnect = useCallback(
    (oldEdge, connection) => {
      if (!workflow || !connection.source || !connection.target) return;
      onWorkflowChange({
        ...workflow,
        edges: workflow.edges.map(e =>
          e.id === oldEdge.id
            ? {
                ...e,
                source: connection.source!,
                target: connection.target!,
              }
            : e,
        ),
      });
    },
    [workflow, onWorkflowChange],
  );

  const onEdgeDoubleClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      if (!workflow) return;
      const current = workflow.edges.find(e => e.id === edge.id);
      if (!current) return;

      const nextCondition = window.prompt(
        'Edge condition (leave empty for none):',
        current.condition || '',
      );
      if (nextCondition === null) return; // user cancelled

      const normalized = nextCondition.trim();
      onWorkflowChange({
        ...workflow,
        edges: workflow.edges.map(e =>
          e.id === edge.id
            ? {
                ...e,
                condition: normalized || undefined,
              }
            : e,
        ),
      });
    },
    [workflow, onWorkflowChange],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!workflow) return;

      const nodeType = event.dataTransfer.getData('application/workflow-node-type');
      if (!nodeType) return;

      const info = nodeTypes[nodeType];
      if (!info) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newId = `${nodeType}_${Date.now()}`;
      const newNode = {
        id: newId,
        type: nodeType,
        label: info.label,
        prompt: info.default_prompt,
        config: {},
        position: { x: position.x, y: position.y },
      };

      onWorkflowChange({
        ...workflow,
        nodes: [...workflow.nodes, newNode],
      });
    },
    [workflow, nodeTypes, screenToFlowPosition, onWorkflowChange],
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeSelect(node.id);
    },
    [onNodeSelect],
  );

  const onPaneClick = useCallback(() => {
    onNodeSelect(null);
  }, [onNodeSelect]);

  const onNodesDelete = useCallback(
    (deletedNodes: Node[]) => {
      if (!workflow) return;
      if (isEditableElement(document.activeElement)) return;
      const deletedIds = new Set(deletedNodes.map(n => n.id));
      onWorkflowChange({
        ...workflow,
        nodes: workflow.nodes.filter(n => !deletedIds.has(n.id)),
        edges: workflow.edges.filter(
          e => !deletedIds.has(e.source) && !deletedIds.has(e.target),
        ),
      });
    },
    [workflow, onWorkflowChange],
  );

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={rfNodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onEdgesDelete={onEdgesDelete}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onEdgeDoubleClick={onEdgeDoubleClick}
        onNodeDragStop={onNodeDragStop}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodesDelete={onNodesDelete}
        fitView
        deleteKeyCode={['Delete', 'Backspace']}
        edgesReconnectable
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1e293b" />
        <Controls />
        <MiniMap
          nodeColor={() => '#334155'}
          maskColor="rgba(15, 23, 42, 0.8)"
          style={{ background: '#1e293b' }}
        />
      </ReactFlow>
    </div>
  );
}
